import { Row } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { useContext, useEffect, useMemo } from "react";
import Form from "react-bootstrap/Form";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";
import { getCurrentDashboardProps } from "util/getCurrentDashboard";
import { findDashboardGroupById, getValidDashboards } from "util/dashboardGroups";

import { Slider, Switch } from "../ToggleSetting";

interface RememberSettingsProps {
  widgetName: string;
  widgetDisplayName: string;
}

export const RememberSettings: WidgetSettingFC<boolean, RememberSettingsProps> = (props) => {
  const { info, settings } = props;
  const { data: globalData, setData: setGlobalData } = useContext(DashboardContext);
  const activeGroup = findDashboardGroupById(
    globalData.dashboardGroups,
    globalData.activeDashboardGroupId,
  );
  const dashboards = getValidDashboards(activeGroup?.dashboards);
  const isChecked = useMemo(() => {
    const dashboard = getCurrentDashboardProps(globalData.activeDashboardId, dashboards);

    return dashboard.rememberSettings[info.widgetName]?.isOn;
  }, [globalData.activeDashboardId, globalData.dashboardGroups, info.widgetName]);
  const currentDashboard = getCurrentDashboardProps(globalData.activeDashboardId, dashboards);

  const handleChange = () => {
    if (!activeGroup || !dashboards.some((dashboard) => dashboard.id === currentDashboard.id)) {
      return;
    }
    const isOn = currentDashboard.rememberSettings[info.widgetName]?.isOn;

    const newData = activeGroup.dashboards.map((dashboard) => {
      if (dashboard?.id === currentDashboard.id) {
        return {
          ...dashboard,
          rememberSettings: {
            ...currentDashboard.rememberSettings,
            [info.widgetName]: {
              isOn: !isOn,
              settings: !isOn ? currentDashboard.rememberSettings[info.widgetName].settings : {},
            },
          },
        };
      }

      return dashboard;
    });

    const newDashboardGroups = globalData.dashboardGroups.map((group) => {
      return group?.id === globalData.activeDashboardGroupId
        ? {
            ...group,
            dashboards: newData,
          }
        : group;
    });

    setGlobalData({
      dashboardGroups: newDashboardGroups,
    });
  };

  useEffect(() => {
    if (!activeGroup || !dashboards.some((dashboard) => dashboard.id === currentDashboard.id)) {
      return;
    }
    const isOn = currentDashboard.rememberSettings[info.widgetName]?.isOn;

    if (!isOn) {
      return;
    }
    // TODO change it after implementing wordpress
    const newData = activeGroup.dashboards.map((dashboard) => {
      if (dashboard?.id === currentDashboard.id) {
        return {
          ...dashboard,
          rememberSettings: {
            ...currentDashboard.rememberSettings,
            [info.widgetName]: {
              isOn,
              settings,
            },
          },
        };
      }

      return dashboard;
    });

    const newDashboardGroups = globalData.dashboardGroups.map((group) => {
      return group?.id === globalData.activeDashboardGroupId
        ? {
            ...group,
            dashboards: newData,
          }
        : group;
    });

    setGlobalData({
      dashboardGroups: newDashboardGroups,
    });
  }, [settings]);

  return (
    <Box>
      <Title>Remember Settings</Title>

      <p>
        If enabled, new {info.widgetDisplayName} widgets will use the same settings as the previous
        one.
      </p>

      <ToggleBox>
        <Switch>
          <Form.Control checked={isChecked} type="checkbox" onChange={handleChange} />
          <Slider />
        </Switch>

        <span>{isChecked ? "Enabled" : "Disabled"}</span>
      </ToggleBox>
    </Box>
  );
};

const Box = styled.div`
  margin-bottom: 3rem;
`;

const Title = styled.div`
  color: var(--secondary);
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 12pt;
`;

export const ToggleBox = styled(Row)`
  align-items: center;
  margin-top: 1rem;
  gap: 10px;

  span {
    color: var(--gray);
  }
`;
